import { BCMSDefaultModel, BCMSRecord, ModalOpen, useRouteData, useWorkflowStep } from "@bcm-dev/bcms-vite";
import { getDefaultBiller, getWorkflowSteps } from "compid/workflows/AddBillerLong";
import { Bill, Biller } from "model";

import CustomSelect, { CustomSelectItem } from "compid/custom-ui/CustomSelect";
import ButtonContainer from "compid/shared/containers/ButtonContainer";
import UIButton from "compid/ui/UIButton";
import UIPhoneInput from "compid/ui/UIPhoneInput";
import UITextInput from "compid/ui/UITextInput";
import React from "react";

/**
 * @function AddCompanyForm
 * @returns {React.Component}
 * @description The main component for adding a new company biller
 * @example
 * <AddCompanyForm />
 */
export default function AddCompanyForm(props) {
  // Initalize the workflow step 
  const workflowStep = useWorkflowStep(getDefaultBiller, getWorkflowSteps);
  // Get the state passed in the route
  const { route } = useRouteData();
  const { biller, bill } = workflowStep.getStepState;
  // update :: update the biller in the state based on the prop and value
  const update = prop => value => workflowStep.update([ "biller", prop ], value);
  const submit = evt => {
    const defaultBiller = BCMSDefaultModel.biller();
    const mergeRecords = BCMSDefaultModel.mergeRecords(biller, defaultBiller);

    const updatedBiller = Biller.init({ ...mergeRecords, "billerType": "company" });
    updatedBiller.number = updatedBiller.number.indexOf("*") === -1 ? `*${ updatedBiller.number }` : updatedBiller.number;

    // Create the biller and bill in the global state
    BCMSRecord.batch([
      Biller.create(updatedBiller),
      Bill.create({ ...bill.record, "billBillerID" : updatedBiller.id })
    ], workflowStep.globalController);
    // Navigate to the make a payment workflow passing the biller in state
    workflowStep.navigate(`/makeAPayment/${ bill.id }`, { "state" : { "addedBiller" : updatedBiller.record } });
  };
  // If the biller name is undefined and a name has been bassed through the route
  // update the biller to set the name
  if(biller.name === undefined && (route !== null && route.name)) {
    workflowStep.update([ "biller", "name" ], route.name);
  }

  // "Leave" button is clicked in the "Are you sure you want to leave?" modal.
  const onLeave = () => workflowStep.navigate("/PayBills");

  return (
    <div className="form-group flex-container flex-vertical add-biller-form-widget">
      <h3 className="form-section-header semi-bold-text">Account information</h3>
      <UITextInput placeHolder="Biller Name" value={ biller.name } onChange={ update("name") } />
      <UITextInput placeHolder="Account number" value={ biller.number } onChange={ update("number") } dataPoint="AccountNumber" />
      <UITextInput placeHolder="Nickname (optional)" value={ biller.nickname } onChange={ update("nickname") } dataPoint="Nickname" />

      <h3 className="form-section-header semi-bold-text">Mailing address</h3>
      <UITextInput placeHolder="Biller address 1" value={ biller.address } onChange={ update("address") } dataPoint="Address" />
      <UITextInput placeHolder="Address 2 (optional)" />
      <UITextInput placeHolder="City" value={ biller.city } onChange={ update("city") } />

      <div className="form-row flex-height">
        <CustomSelect label="State" helpText="" value={ biller.state } onChange={ update("state") }>
            <CustomSelectItem value="AL">Alabama</CustomSelectItem>
            <CustomSelectItem value="AK">Alaska</CustomSelectItem>
            <CustomSelectItem value="AZ">Arizona</CustomSelectItem>
            <CustomSelectItem value="AR">Arkansas</CustomSelectItem>
            <CustomSelectItem value="CA">California</CustomSelectItem>
            <CustomSelectItem value="CO">Colorado</CustomSelectItem>
            <CustomSelectItem value="CT">Connecticut</CustomSelectItem>
            <CustomSelectItem value="DE">Delaware</CustomSelectItem>
            <CustomSelectItem value="DC">District Of Columbia</CustomSelectItem>
            <CustomSelectItem value="FL">Florida</CustomSelectItem>
            <CustomSelectItem value="GA">Georgia</CustomSelectItem>
            <CustomSelectItem value="HI">Hawaii</CustomSelectItem>
            <CustomSelectItem value="ID">Idaho</CustomSelectItem>
            <CustomSelectItem value="IL">Illinois</CustomSelectItem>
            <CustomSelectItem value="IN">Indiana</CustomSelectItem>
            <CustomSelectItem value="IA">Iowa</CustomSelectItem>
            <CustomSelectItem value="KS">Kansas</CustomSelectItem>
            <CustomSelectItem value="KY">Kentucky</CustomSelectItem>
            <CustomSelectItem value="LA">Louisiana</CustomSelectItem>
            <CustomSelectItem value="ME">Maine</CustomSelectItem>
            <CustomSelectItem value="MD">Maryland</CustomSelectItem>
            <CustomSelectItem value="MA">Massachusetts</CustomSelectItem>
            <CustomSelectItem value="MI">Michigan</CustomSelectItem>
            <CustomSelectItem value="MN">Minnesota</CustomSelectItem>
            <CustomSelectItem value="MS">Mississippi</CustomSelectItem>
            <CustomSelectItem value="MO">Missouri</CustomSelectItem>
            <CustomSelectItem value="MT">Montana</CustomSelectItem>
            <CustomSelectItem value="NE">Nebraska</CustomSelectItem>
            <CustomSelectItem value="NV">Nevada</CustomSelectItem>
            <CustomSelectItem value="NH">New Hampshire</CustomSelectItem>
            <CustomSelectItem value="NJ">New Jersey</CustomSelectItem>
            <CustomSelectItem value="NM">New Mexico</CustomSelectItem>
            <CustomSelectItem value="NY">New York</CustomSelectItem>
            <CustomSelectItem value="NC">North Carolina</CustomSelectItem>
            <CustomSelectItem value="ND">North Dakota</CustomSelectItem>
            <CustomSelectItem value="OH">Ohio</CustomSelectItem>
            <CustomSelectItem value="OK">Oklahoma</CustomSelectItem>
            <CustomSelectItem value="OR">Oregon</CustomSelectItem>
            <CustomSelectItem value="PA">Pennsylvania</CustomSelectItem>
            <CustomSelectItem value="RI">Rhode Island</CustomSelectItem>
            <CustomSelectItem value="SC">South Carolina</CustomSelectItem>
            <CustomSelectItem value="SD">South Dakota</CustomSelectItem>
            <CustomSelectItem value="TN">Tennessee</CustomSelectItem>
            <CustomSelectItem value="TX">Texas</CustomSelectItem>
            <CustomSelectItem value="UT">Utah</CustomSelectItem>
            <CustomSelectItem value="VT">Vermont</CustomSelectItem>
            <CustomSelectItem value="VA">Virginia</CustomSelectItem>
            <CustomSelectItem value="WA">Washington</CustomSelectItem>
            <CustomSelectItem value="WV">West Virginia</CustomSelectItem>
            <CustomSelectItem value="WI">Wisconsin</CustomSelectItem>
            <CustomSelectItem value="WY">Wyoming</CustomSelectItem>
        </CustomSelect> 
      </div>

      <div className="flex-container flex-horizontal">
        <UITextInput placeHolder="ZIP code (5-digit)" value={ biller.zip } onChange={ update("zip") } />
        <UITextInput placeHolder="+ZIP 4 (optional)" />
      </div>

      <UIPhoneInput placeHolder="Biller phone number" value={ biller.phone } onChange={ update("phone") } dataPoint="PhoneNumber" />

      <ButtonContainer className="form-button-container button-container flex-container flex-horizontal">
        <UIButton className="secondary-button">
          <ModalOpen id="WantToLeaveModal" data={ {onLeave: onLeave} }>Cancel</ModalOpen>
        </UIButton>
        <UIButton className="primary-button" onClick={ submit } dataPoint="Add">Add</UIButton>
      </ButtonContainer>
    </div>
  )
}